// GridSelector.tsx
import React, { useState, useEffect, useRef } from 'react';

// Props definition for the GridSelector
interface GridSelectorProps {
    rows: number;
    cols: number;
    onSelectionChange: (rows: number, cols: number) => void;
}

const MAX_GRID_SIZE = 20;
const INITIAL_GRID_SIZE = 5;

export const GridSelector: React.FC<GridSelectorProps> = ({ rows, cols, onSelectionChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [gridSize, setGridSize] = useState({ rows: INITIAL_GRID_SIZE, cols: INITIAL_GRID_SIZE });
    const [selection, setSelection] = useState({ rows, cols });
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Effect to handle clicks outside the component to close the popup
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    
    useEffect(() => {
        if (isOpen) {
            const newGridRows = Math.min(MAX_GRID_SIZE, Math.max(INITIAL_GRID_SIZE, rows + 1));
            const newGridCols = Math.min(MAX_GRID_SIZE, Math.max(INITIAL_GRID_SIZE, cols + 1));
            setGridSize({ rows: newGridRows, cols: newGridCols });
        } else {
            setSelection({ rows, cols });
        }
    }, [isOpen, rows, cols]);

    const handleMouseOver = (rowIndex: number, colIndex: number) => {
        const newSelection = { rows: rowIndex + 1, cols: colIndex + 1 };
        setSelection(newSelection);
        let nextRows = gridSize.rows;
        let nextCols = gridSize.cols;
        if (newSelection.rows === gridSize.rows && gridSize.rows < MAX_GRID_SIZE) {
            nextRows++;
        }
        if (newSelection.cols === gridSize.cols && gridSize.cols < MAX_GRID_SIZE) {
            nextCols++;
        }
        if (nextRows !== gridSize.rows || nextCols !== gridSize.cols) {
            setGridSize({ rows: nextRows, cols: nextCols });
        }
    };
    
    const handleClick = (rowIndex: number, colIndex: number) => {
        const newRows = rowIndex + 1;
        const newCols = colIndex + 1;
        onSelectionChange(newRows, newCols);
        setIsOpen(false);
    };

    const toggleOpen = () => {
        setIsOpen(prev => !prev);
    };

    return (
        <div className='ll-grid-selector-wrapper' ref={wrapperRef}>
            <button type='button' className='ll-button ll-grid-selector-button' onClick={toggleOpen}>
                <GridIcon />
            </button>
            {isOpen && (
                <div className='ll-grid-selector-popup'>
                    <div
                        className='ll-grid-container'
                        style={{
                            gridTemplateRows: `repeat(${gridSize.rows}, 1fr)`,
                            gridTemplateColumns: `repeat(${gridSize.cols}, 1fr)`,
                        }}
                    >
                        {Array.from({ length: gridSize.rows }).flatMap((_, rowIndex) =>
                            Array.from({ length: gridSize.cols }).map((_, colIndex) => {
                                const isSelected = rowIndex < selection.rows && colIndex < selection.cols;
                                return (
                                    <div
                                        key={`${rowIndex}-${colIndex}`}
                                        className={`ll-grid-cell ${isSelected ? 'll-selected' : ''}`}
                                        onMouseOver={() => handleMouseOver(rowIndex, colIndex)}
                                        onClick={() => handleClick(rowIndex, colIndex)}
                                    />
                                );
                            })
                        )}
                    </div>
                    <div className='ll-grid-selector-label'>
                        {selection.rows}x{selection.cols}
                    </div>
                </div>
            )}
        </div>
    );
};

// SVG Icon
const GridIcon = () => (
    <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path d='M0 2.5C0 2.22386 0.223858 2 0.5 2H3.5C3.77614 2 4 2.22386 4 2.5V5.5C4 5.77614 3.77614 6 3.5 6H0.5C0.223858 6 0 5.77614 0 5.5V2.5Z' fill='currentColor'/>
        <path d='M0 10.5C0 10.2239 0.223858 10 0.5 10H3.5C3.77614 10 4 10.2239 4 10.5V13.5C4 13.7761 3.77614 14 3.5 14H0.5C0.223858 14 0 13.7761 0 13.5V10.5Z' fill='currentColor'/>
        <path d='M6 2.5C6 2.22386 6.22386 2 6.5 2H9.5C9.77614 2 10 2.22386 10 2.5V5.5C10 5.77614 9.77614 6 9.5 6H6.5C6.22386 6 6 5.77614 6 5.5V2.5Z' fill='currentColor'/>
        <path d='M6 10.5C6 10.2239 6.22386 10 6.5 10H9.5C9.77614 10 10 10.2239 10 10.5V13.5C10 13.7761 9.77614 14 9.5 14H6.5C6.22386 14 6 13.7761 6 13.5V10.5Z' fill='currentColor'/>
        <path d='M12 2.5C12 2.22386 12.2239 2 12.5 2H15.5C15.7761 2 16 2.22386 16 2.5V5.5C16 5.77614 15.7761 6 15.5 6H12.5C12.2239 6 12 5.77614 12 5.5V2.5Z' fill='currentColor'/>
        <path d='M12 10.5C12 10.2239 12.2239 10 12.5 10H15.5C15.7761 10 16 10.2239 16 10.5V13.5C16 13.7761 15.7761 14 15.5 14H12.5C12.2239 14 12 13.7761 12 13.5V10.5Z' fill='currentColor'/>
    </svg>
);